import axios, { CancelTokenSource } from 'axios';
import Input from 'components/final-form/Input';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import BookingHistoryView from 'components/patient-entry/BookingHistoryView';
import { FormApi, FormState } from 'final-form';
import moment from 'moment';
import React, { useState } from "react";
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Icon, Loader, Message, Popup } from 'semantic-ui-react';
import { getInterventionBookingHistory } from 'services/interventionBookingServices';
import { getCovidTestOverview, setProductNoPoolAvailableTestkit } from 'services/labCentralServices';
import styled from 'styled-components';
import { debounce } from 'ts-debounce';
import { InterventionBookingStatus, Urgency } from 'ts-types/api.enums';
import { ConfirmReceptionRequest, InterventionBookingHistoryDto, LabCentralSearchResult } from 'ts-types/api.types';
import { isValidBarcode } from 'utils/barcodeUtils';
import { isoToDisplayDate } from 'utils/dateUtils';
import { errorUtils } from 'utils/errorUtils';
import { required } from 'utils/validatorUtils';

export const StyledTable = styled(VirtualizedTable)`
  .booking-history-icon-container {
    display: inline-block;
  }

  .expected-date {
    display: inline-block;
  }

  .expected-date.warn {
    color: #a76f0b;
  }

  .expected-date.overdue {
    color: darkred;
  }
`;

const StyledColumn = styled(Grid.Column)`
  & .ui.button:first-child {
    margin-right: 1.5rem;
  }
`;

const validBarCode = (value: string) => {
  const required = value ? undefined : "required";
  if (required) {
    return required;
  }

  const validBarcode = isValidBarcode(value);
  return validBarcode ? undefined : "invalidCode";
};

interface NoAvailablePoolTestkitRequest {
  testKitCode: string
}

interface TestKitReceptionFieldProps extends FormState {
  values: Partial<NoAvailablePoolTestkitRequest>
}

interface Props extends MessageSourceProps {
}

let initialValues: Partial<ConfirmReceptionRequest> = {};

const NoPoolAvailableTestkitView = (props: Props) => {

  const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

  const {getMessage} = props;

  const [covidTestData, setCovidTestData] = useState([] as LabCentralSearchResult[]);

  const [interventionBookingHistory, setInterventionBookingHistory] = useState([] as InterventionBookingHistoryDto[]);
  const [interventionBookingHistoryLoading, setInterventionBookingHistoryLoading] = useState(false);

  const [inProgress, setInProgress] = useState(false);
  const [successfullySetProduct, setSuccessfullySetProduct] = useState(undefined as undefined | string);
  const [errorMessage, setErrorMessage] = useState(undefined as string | undefined);
  const [warningMessage, setWarningMessage] = useState(undefined as string | undefined);

  const onFinally = () => {
    setInProgress(false)
  };

  const handleChange = debounce(({values}: TestKitReceptionFieldProps): void => {

    if (values.testKitCode && isValidBarcode(values.testKitCode)
        && (!covidTestData.length || (covidTestData[0].testKitCode !== values.testKitCode))) {

      setErrorMessage(undefined);
      setWarningMessage(undefined);
      setSuccessfullySetProduct(undefined);
      setInProgress(true);

      getCovidTestOverview(values.testKitCode, cancelTokenSource)
          .then(response => setCovidTestData([response]))
          .catch((e) => {
            handleError(e.response.data);
            onFinally();
          })
          .finally(onFinally);
    } else if ((!values.testKitCode || !isValidBarcode(values.testKitCode))) {
      setCovidTestData([]);
    }
  }, 300);

  const resetFormAndCovidTestData = (form: FormApi) => {
    form.reset();
    setCovidTestData([]);
    setWarningMessage(undefined);
    setErrorMessage(undefined);
  };

  const handleError = (error: any) => {
    const errorCode = error.errorCode;
    const knownWarnings: Array<String> = [
      errorUtils.noPoolInterventionBookingAlreadyHasProduct
    ];
    const knownErrors: Array<String> = [
      errorUtils.canNotSetProduct,
      errorUtils.noPoolInterventionBookingAlreadyHasProduct,
      errorUtils.requiredSpecialProductNotFound,
      errorUtils.invalidBarcode,
      errorUtils.unknownBarcode,
      errorUtils.unknownInterventionBookingId,
      errorUtils.unknownPatient
    ];
    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownWarnings.includes(violation.errorCode)) {
          setWarningMessage(getMessage(`error.${violation.errorCode}`))
        } else  if (knownErrors.includes(violation.errorCode)) {
          setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      if (knownWarnings.includes(errorCode)) {
        setWarningMessage(getMessage(`error.${errorCode}`))
      } else if (knownErrors.includes(errorCode)) {
        setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        setErrorMessage(getMessage('error.general'));
      }
    }
  }

  const handleSubmit = async (values: Partial<NoAvailablePoolTestkitRequest>, form: FormApi) => {

    if (covidTestData.length
        && InterventionBookingStatus.LAB_RECEIVED === covidTestData[0].status) {

      setWarningMessage(undefined);
      setErrorMessage(undefined);

      await setProductNoPoolAvailableTestkit(covidTestData[0].testKitCode, cancelTokenSource)
          .then(response => {
            setSuccessfullySetProduct(response)
            form.reset();
          })
          .catch((e) => handleError(e.response.data));
    }
  };

  const renderMarkNoPoolAvailableFormSegment = (): JSX.Element => {
    return (
        <FinalForm
            onSubmit={handleSubmit}
            initialValues={initialValues}
            subscription={{values: true, pristine: true, submitting: true}}
            render={renderForm}
        />
    );
  };

  const renderForm = ({submitting, handleSubmit, form}: FormRenderProps): React.ReactNode => {

    const disableBtn = (submitting || form.getState().hasValidationErrors)
        || !covidTestData.length
        || InterventionBookingStatus.LAB_RECEIVED !== covidTestData[0].status;

    return (
        <form onSubmit={handleSubmit}>
          <PxGrid columns={3}>
            {errorMessage &&
                <Grid.Row>
                  <Grid.Column width={16}>
                    <StyledErrorMessage onDismiss={() => setErrorMessage(undefined)}>
                      {errorMessage}
                    </StyledErrorMessage>
                  </Grid.Column>
                </Grid.Row>
            }
            {warningMessage &&
                <Grid.Row>
                  <Grid.Column width={16}>
                    <StyledErrorMessage warning={true} onDismiss={() => setWarningMessage(undefined)}>
                      {warningMessage}
                    </StyledErrorMessage>
                  </Grid.Column>
                </Grid.Row>
            }
            {successfullySetProduct &&
                <Grid.Row>
                  <Grid.Column width={16}>
                    <Message success onDismiss={() => setSuccessfullySetProduct(undefined)}>
                      <p>
                        {getMessage(`labCentral.noPoolAvailableTestkit.${successfullySetProduct}`)}
                      </p>
                    </Message>
                  </Grid.Column>
                </Grid.Row>
            }
            <Grid.Row>
              <Grid.Column width={2} verticalAlign="middle">
                {getMessage("patientRecord.testResult.testKitCode")}:
              </Grid.Column>
              <Grid.Column width={5}>
                <Field
                    name="testKitCode"
                    component={Input}
                    validate={validBarCode}
                    autoFocus
                    fluid
                />
              </Grid.Column>
              <StyledColumn width={9}>
                <Button compact type="submit" primary disabled={disableBtn}>
                  {getMessage("labCentral.noPoolAvailableTestkit.mark")}
                </Button>
                <Button compact
                        type="button"
                        secondary
                        onClick={() => resetFormAndCovidTestData(form)}
                        disabled={submitting}>
                  {getMessage("button.reset")}
                </Button>
              </StyledColumn>
            </Grid.Row>
            <FormSpy subscription={{values: true}} onChange={handleChange}/>
          </PxGrid>
        </form>
    )
  };

  const covidTestDataRowGetter = ({index}: any) => {
    Object.assign(covidTestData[index], {index: index + 1});
    return covidTestData[index];
  };

  const covidTestDataRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};

    let styles = {...style};

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={styles}
        >
          {columns}
        </div>
    );
  };

  const onStatusColumnClick = (bookingId: number) => {
    setInterventionBookingHistoryLoading(true);
    getInterventionBookingHistory(bookingId, cancelTokenSource)
        .then(response => {
          setInterventionBookingHistory(response);
        })
        .finally(() => {
          setInterventionBookingHistoryLoading(false);
        });
  };

  const renderInterventionBookingHistory = (data: any) => {
    const {rowData} = data;

    const trigger = (
        <div className="booking-history-icon-container">
          <div>
            <Icon
                className="booking-history-icon"
                name="history"
                onClick={() => onStatusColumnClick(rowData.bookingId)}/>
          </div>
        </div>
    )

    const loader = (
        <Loader active inline="centered" size="small">{getMessage("loading")}</Loader>
    );

    const content =
        interventionBookingHistoryLoading ? loader
            : <BookingHistoryView bookingHistory={interventionBookingHistory} currentStatus={rowData.status}/>

    return (
        <span>
      <Popup
          trigger={trigger}
          popperDependencies={[!!interventionBookingHistoryLoading]}
          popperModifiers={{
            preventOverflow: {
              boundary: document.getElementById("testKitReceptionMainContent")
            }
          }}
          content={content}
          on="click"
          size="small"
          position="left center"
      />
    </span>
    );
  }

  const testKitCodeCellRenderer = (data: any) => {
    const {cellData} = data;
    if (!cellData) {
      return "";
    }
    return cellData;
  };

  const interventionStatusCellRenderer = (data: any) => {
    const {rowData} = data;

    const status = rowData.status;

    return <div>
      {getMessage(`enum.interventionBookingStatus.${status}`)}
      &nbsp;{renderInterventionBookingHistory(data)}
    </div>;
  }

  const entryDateCellRenderer = (data: any) => {

    const {rowData} = data;

    const bookingDate = isoToDisplayDate(rowData.bookingDate);
    const bookingTime = rowData.bookingTime;
    const entryDateTimeTitle = `${bookingDate} - ${bookingTime}`;

    return (
        <div>
          <div className="date birth-date" title={entryDateTimeTitle}>
            {bookingDate} {bookingTime}
          </div>
        </div>
    )
  };

  const inputChannelCellRenderer = (data: any) => {

    const {rowData} = data;

    const inputChannel = rowData.inputChannel;
    const inputChannelDisplay = getMessage(`enum.interventionBookingInputChannel.${inputChannel}`);

    return (
        <div>
          <div className="date birth-date" title={inputChannelDisplay}>{inputChannelDisplay}</div>
        </div>
    )
  };

  const patientBirthDateCellRenderer = (data: any) => {

    const {rowData} = data;

    const birthDate = rowData.patientBirthDate
        ? `${isoToDisplayDate(rowData.patientBirthDate)} (${rowData.patientAge})`
        : "";

    return (
        <div>
          <div className="date birth-date" title={birthDate}>{birthDate}</div>
        </div>
    )
  };

  const urgencyCellRenderer = (data: any) => {
    const {getMessage, getMessageWithNamedParams} = props;
    const {cellData, rowData} = data;

    const expectedResultTime = rowData.expectedResultTime;
    const expectedResultDate = rowData.expectedResultDate;
    let expectedResultDateTime = <></>;
    if (expectedResultDate && expectedResultTime) {

      const expectedResDate = moment(expectedResultDate)
      let numberOfDays = undefined;
      if (moment(expectedResultDate).isAfter()) {
        numberOfDays = expectedResDate.diff(moment(), 'days') + 1;
      }

      const expectedTime = `${expectedResultTime}`;
      const expectedDateTime = `${isoToDisplayDate(expectedResultDate)} - ${expectedTime}`;

      const expectedInstant = moment(`${expectedResultDate} ${expectedResultTime}`);
      const minutesToResult = expectedInstant.diff(moment(), 'minutes');
      let textClass = 'normal';
      if (minutesToResult > 0 && minutesToResult < 30) {
        textClass = 'warn';
      } else if (minutesToResult < 0) {
        textClass = 'overdue';
      }

      expectedResultDateTime = (
          <div className={`expected-date ${textClass}`} title={expectedDateTime}>
            {expectedResultTime}
            {numberOfDays && getMessageWithNamedParams(
                'patientCentral.overview.row.days',
                {numberOfDays: numberOfDays}
            )
            }
          </div>
      );
    }

    return cellData && cellData === Urgency.HIGH
        ? <>
          {expectedResultDateTime}
          <Icon name="exclamation triangle"
                color="red"
                style={{marginLeft: ".5rem"}}
                title={`${getMessage("patientCentral.checkIn.urgency")}: ${getMessage("enum.urgency.HIGH")}`}
          />
        </>
        : <>{expectedResultDateTime}</>;
  }

  const patientNameCellRenderer = ({rowData}: any) => {
    let patientFullName = "";
    if (rowData.patientLastName) {
      patientFullName = rowData.patientLastName;
    }

    if (rowData.patientFirstName) {
      patientFullName = `${patientFullName} ${rowData.patientFirstName}`
    }
    return patientFullName;
  }

  const companyTypeCellRenderer = ({rowData}: any) => {
    let companyType = "";

    if (rowData.companyType) {
      companyType = getMessage("enum.companyType." + rowData.companyType);
    }

    return companyType;
  }

  const cantonCellRenderer = ({rowData}: any) => {
    if (rowData.canton) {
      return rowData.canton;
    }

    return "";
  }

  const companyNameCellRenderer = ({rowData}: any) => {
    if (rowData.companyDescription) {
      return rowData.companyDescription;
    }

    return "";
  }

  const renderCovidTestDataSegment = (): JSX.Element => {

    const tableColumns = [
      {
        width: 100,
        flexGrow: 0,
        flexShrink: 0,
        label: "",
        dataKey: "urgency",
        cellRenderer: urgencyCellRenderer
      },
      {
        width: 80,
        flexGrow: 0,
        flexShrink: 0,
        label: getMessage("patientCentral.overview.tableHeader.patientId"),
        dataKey: "patientId"
      },
      {
        width: 150,
        flexGrow: 2,
        flexShrink: 0,
        label: `${getMessage("patientCentral.overview.tableHeader.patientFirstName")} ${getMessage("patientCentral.overview.tableHeader.patientLastName")}`,
        dataKey: "patientId",
        cellRenderer: patientNameCellRenderer
      },
      {
        width: 150,
        flexGrow: 2,
        flexShrink: 0,
        label: `${getMessage("labCentral.testkitReceptionTable.companyType")}`,
        dataKey: "companyType",
        cellRenderer: companyTypeCellRenderer
      },
      {
        width: 80,
        flexGrow: 0,
        flexShrink: 0,
        label: getMessage("labCentral.testkitReceptionTable.canton"),
        dataKey: "canton",
        cellRenderer: cantonCellRenderer
      },
      {
        width: 150,
        flexGrow: 2,
        flexShrink: 0,
        label: getMessage("patientCentral.overview.tableHeader.company"),
        dataKey: "companyDescription",
        cellRenderer: companyNameCellRenderer
      },
      {
        width: 120,
        flexGrow: 0,
        flexShrink: 0,
        label: getMessage("patientCentral.overview.tableHeader.patientBirthDate"),
        dataKey: "patientBirthDate",
        cellRenderer: patientBirthDateCellRenderer
      },
      {
        width: 100,
        flexGrow: 0,
        flexShrink: 0,
        label: getMessage("patientCentral.overview.tableHeader.inputChannel"),
        dataKey: "inputChannel",
        cellRenderer: inputChannelCellRenderer
      },
      {
        width: 150,
        flexGrow: 0,
        flexShrink: 0,
        label: getMessage("enum.testDateSearchBy.BOOKING_DATE"),
        dataKey: "bookingDate",
        cellRenderer: entryDateCellRenderer
      },
      {
        width: 170,
        flexGrow: 1,
        flexShrink: 0,
        label: getMessage("patientCentral.overview.tableHeader.testKitCode"),
        dataKey: "testKitCode",
        cellRenderer: testKitCodeCellRenderer
      },
      {
        width: 130,
        flexGrow: 0,
        flexShrink: 0,
        label: getMessage("patientCentral.overview.tableHeader.status"),
        dataKey: "status",
        cellRenderer: interventionStatusCellRenderer
      }
    ];

    return (
        <StyledTable
            rowCount={covidTestData.length}
            rowGetter={covidTestDataRowGetter}
            rowRenderer={covidTestDataRowRenderer}
            rowHeight={42}
            columns={tableColumns}
        />
    );
  };

  return (
      <PraxStyledSegment style={{height: "100%"}} id="testKitReceptionMainContent">
        <div>
          {renderMarkNoPoolAvailableFormSegment()}
        </div>
        <StyledViewTableRow>
          {inProgress
              ? <LoaderComponent message={getMessage("loading")}/>
              : covidTestData.length
                  ? renderCovidTestDataSegment()
                  : null}
        </StyledViewTableRow>
      </PraxStyledSegment>
  );

}

export default withMessages(NoPoolAvailableTestkitView);
